import React, { useEffect, useState } from 'react';

import { approverTooltipContent } from '../../user/approverTooltip';
import UserApprovalListBudgetChangesApprovalsCompactRow from '../../user/budget_approvals/UserApprovalListBudgetChangesApprovalsCompactRow';
import UserApprovalListExpenseFundApprovalsRow from '../../user/UserApprovalListExpenseFundApprovalsRow';
import UserApprovalListPositionFundApprovalsRow from '../../user/UserApprovalListPositionFundApprovalsRow';
import UserApprovalListPurchaseOrdersRow from '../../user/UserApprovalListPurchaseOrdersRow';
import UserApprovalListPurchaseRequisitionsRow from '../../user/UserApprovalListPurchaseRequisitionsRow';
import ApprovalDetailModal from '../approvals/ApprovalDetailModal';
import { OVERDUE_ROW_CLASS } from '../HomeSection';

import ApprovalCommentLine from './ApprovalCommentLine';
import ApprovalOutcomeChip from './ApprovalOutcomeChip';
import QueueFilters from './QueueFilters';
import QueueTable from './QueueTable';
import {
    isOverThreshold,
    OUTCOME_PENDING,
    queueRowKey,
    STATUS_PENDING,
    statusFilterLabel,
    TYPE_BUDGET_CHANGE,
    TYPE_EXPENSE_FUND,
    TYPE_POSITION_FUND,
    TYPE_PURCHASE_ORDER,
    TYPE_PURCHASE_REQUISITION,
    typeChipLabel,
    waitingOrActionedLabel,
} from './queueTypes';
import useHomeQueue from './useHomeQueue';

import useModal from 'Hooks/useModal';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';

/**
 * This approver's whole register across every type — what is waiting on them and
 * what they have already decided — newest first.
 *
 * WHAT THIS FILE DOES NOT DO: approve anything. Every action opens the SAME
 * modal the per-type approval tab opens, against the same toggle endpoint, so
 * there is exactly one implementation of each approval workflow and this page is
 * not it.
 *
 * THE FIRST COLUMN CARRIES TWO DIFFERENT FACTS. A pending row reads how long the
 * item has been with THIS approver — the server's `sort_at` for these types is
 * the `approvables` row's `updated_at`, the instant the chain reached them — and
 * a row they have actioned reads the date they actioned it. Only a pending row
 * takes the red over-SLA wash: a decision taken four months ago is not late.
 *
 * `outcome` DECIDES WHETHER A ROW OFFERS AN ACTION, and it comes from the
 * server. A history row carries no approve control at all.
 *
 * After a modal completes, BOTH the queue page and the entitlement payload are
 * re-read. The tile, the tab badge and the queue all count the same set, and
 * refreshing only one of them is how they come to disagree on screen.
 */
export default function MyApprovalsView({ approvalsQueueUrl, approvalAgeThresholdDays, onQueueChange, refreshToken, myViews }) {
    /* NO page size sent. The register's 25 is the endpoint's own default, and
       naming it again here would be the same number in two files. */
    const queue = useHomeQueue(approvalsQueueUrl, { perPage: null, initialStatus: STATUS_PENDING });

    const tooltipContent = approverTooltipContent(myViews.display_approval_statuses);

    /* useModal, not a hand-rolled flowbite instance: it guards the double-instance
       bug that leaks a backdrop when a modal is shown before the second instance
       settles. One pop-up serves every type and both of Home's tabs. */

    /* The OTHER panel acted. Both are mounted at once once the user has visited
       both tabs, so this one re-reads rather than going on showing a row that is
       no longer waiting. Skipped on mount: the token starts at 0 and the queue
       has just loaded. */
    useEffect(() => {
        if (refreshToken) {
            queue.refresh();
        }
    }, [refreshToken]);

    const detailModal = useModal();

    /* The row the pop-up is open on. One row at a time, so a second open cannot
       leave the first one's record behind. */
    const [activeRow, setActiveRow] = useState(null);

    useEffect(() => {
        activeRow ? detailModal.show() : detailModal.hide();
    }, [activeRow]);

    const open = (row) => setActiveRow(row);

    const close = () => setActiveRow(null);

    /* A completed approval moves out of the Pending bucket, so the page is
       re-read rather than patched, and the entitlement payload with it. */
    const afterAction = () => {
        close();
        queue.refresh();
        onQueueChange?.();
    };

    const columns = [
        { key: 'waiting', label: $t('Waiting / actioned'), className: 'tw-w-32' },
        { key: 'type', label: $t('Type'), className: 'tw-w-44' },
        { key: 'title', label: $t('Title') },
        { key: 'value', label: $t('Value'), className: 'tw-w-32' },
        { key: 'status', label: $t('Status'), className: 'tw-w-40' },
        /* KEPT ALONGSIDE the new Status column, though they read as a pair of
           outcome cells on a history row. Dropping it would leave a pending
           approval with nothing to action it from. */
        { key: 'actions', label: $t('Actions'), className: 'tw-w-48' },
    ];

    const renderRow = (row) => {
        const pending = row.outcome === OUTCOME_PENDING;

        const shared = {
            key: queueRowKey(row),
            row: row.record,
            typeLabel: row.type_label,
            waitingLabel: waitingOrActionedLabel(row),
            hideStatus: true,
            outcomeChip: <ApprovalOutcomeChip outcome={row.outcome} />,
            /* Renders nothing on a pending row, which has no comment of this
               user's. The full text is the detail pop-up's to show. */
            titleSubLine: <ApprovalCommentLine comment={row.comment} />,
            rowClassName: pending && isOverThreshold(row.sort_at, approvalAgeThresholdDays) ? OVERDUE_ROW_CLASS : undefined,
            commonApprovalStatuses: myViews.common_approval_statuses,
            tooltipContent,
            /* Authority comes from register membership — a pending row is one
               where this user is the active PENDING approver. This flag says only
               whether the row carries controls or deep-links. */
            canBeToggled: row.can_act_inline,
            isPending: () => pending,
        };

        switch (row.type) {
            /* Row click and Action open the SAME pop-up. The four view modals
               and four approval modals this replaces were the only reason those
               were ever two different things. */
            case TYPE_PURCHASE_REQUISITION:
                return <UserApprovalListPurchaseRequisitionsRow {...shared} toggle={() => open(row)} onClickHandler={() => open(row)} />;
            case TYPE_PURCHASE_ORDER:
                return <UserApprovalListPurchaseOrdersRow {...shared} toggle={() => open(row)} onClickHandler={() => open(row)} />;
            case TYPE_POSITION_FUND:
                return <UserApprovalListPositionFundApprovalsRow {...shared} toggle={() => open(row)} onClickHandler={() => open(row)} />;
            case TYPE_EXPENSE_FUND:
                return <UserApprovalListExpenseFundApprovalsRow {...shared} toggle={() => open(row)} onClickHandler={() => open(row)} />;
            case TYPE_BUDGET_CHANGE:
                return (
                    <UserApprovalListBudgetChangesApprovalsCompactRow {...shared} href={row.href} toggle={() => open(row)} onClickHandler={() => open(row)} />
                );
            default:
                return null;
        }
    };

    /* An empty table under the DEFAULT filters means the approver has nothing
       waiting; an empty table under any other combination means the filters
       matched nothing. Told apart because the first is good news and the second
       is an instruction to widen the filters. */
    const unfiltered = queue.status === STATUS_PENDING && !queue.type;

    return (
        <div>
            <QueueFilters
                countsByType={queue.countsByType}
                type={queue.type}
                onTypeChange={queue.filterByType}
                status={queue.status}
                onStatusChange={queue.filterByStatus}
            />

            <QueueTable
                columns={columns}
                rows={queue.rows}
                renderRow={renderRow}
                loading={queue.loading}
                error={queue.error}
                total={queue.queue?.total ?? 0}
                from={queue.queue?.from}
                to={queue.queue?.to}
                lastPage={queue.queue?.last_page ?? 1}
                page={queue.page}
                perPage={queue.perPage}
                onPageChange={queue.setPage}
                pagerSummary={`${statusFilterLabel(queue.status)} · ${queue.type ? typeChipLabel(queue.type) : $t('All types')}`}
                emptyState={
                    unfiltered ? (
                        /* ONE all-clear state for every type at once. The layout
                           this replaces showed an approver with an empty queue
                           five separate empty tables. */
                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-8 tw-text-center">
                            <CircleCheckIcon className="tw-h-6 tw-w-6 tw-text-green" aria-hidden="true" />
                            <p className="tw-text-sm tw-font-semibold tw-text-charcoal">{$t('All clear')}</p>
                            <p className="tw-text-xs tw-text-charcoal-60">{$t('Nothing is waiting on your approval right now.')}</p>
                        </div>
                    ) : (
                        <p className="tw-py-8 tw-text-center tw-text-xs tw-text-charcoal-60">{$t('No approvals match the selected filters.')}</p>
                    )
                }
            />

            <ApprovalDetailModal modal={detailModal} row={activeRow} myViews={myViews} onClose={close} onActioned={afterAction} />
        </div>
    );
}
